import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { FiArrowLeft, FiDollarSign, FiUser, FiSend, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../utils/helpers';

const QrTransferScreen = () => {
  const { walletCode } = useParams();
  const { user, userType } = useAuth();
  const navigate = useNavigate();

  const [receiverInfo, setReceiverInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [senderProfile, setSenderProfile] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (walletCode) {
      loadReceiverInfo();
      loadSenderProfile();
    }
  }, [walletCode, user]);

  const loadReceiverInfo = async () => {
    setLoadingInfo(true);
    try {
      const result = await supabaseService.getUserByWalletCode(walletCode);
      if (result) {
        setReceiverInfo(result);
      } else {
        toast.error('لم يتم العثور على مستخدم بهذا الرمز');
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      console.error('Error loading receiver info:', error);
      toast.error('حدث خطأ أثناء تحميل معلومات المستخدم');
    } finally {
      setLoadingInfo(false);
    }
  };

  const loadSenderProfile = async () => {
    if (!user || !userType) return;
    try {
      let profile = null;
      if (userType === 'provider') {
        profile = await supabaseService.getProviderById(user.id);
      } else {
        profile = await supabaseService.getClientById(user.id);
      }
      setSenderProfile(profile);
    } catch (error) {
      console.error('Error loading sender profile:', error);
    }
  };

  const handleSendTransfer = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('يرجى إدخال مبلغ صحيح');
      return;
    }

    const amountNum = parseFloat(amount);

    // Check if sender has enough balance
    if (senderProfile && amountNum > (senderProfile.wallet || 0)) {
      toast.error('رصيد محفظتك غير كافٍ لإتمام التحويل');
      return;
    }

    setSending(true);
    try {
      await supabaseService.transferFunds(user.id, walletCode, amountNum);
      setSuccess(true);
      toast.success('تم تحويل المبلغ بنجاح!');
      
      // Reload sender balance
      loadSenderProfile();
      
      // Auto close after 3 seconds
      setTimeout(() => navigate(-1), 3000);
    } catch (error) {
      console.error('Error transferring funds:', error);
      const msg = error.message || '';
      if (msg.includes('insufficient') || msg.includes('رصيد')) {
        toast.error('رصيد محفظتك غير كافٍ');
      } else if (msg.includes('not found')) {
        toast.error('رمز المحفظة غير صحيح');
      } else {
        toast.error('فشل التحويل. حاول مرة أخرى');
      }
    } finally {
      setSending(false);
    }
  };

  if (loadingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!receiverInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100" dir="rtl">
        <div className="text-center p-8">
          <div className="bg-red-100 rounded-full p-4 inline-block mb-4">
            <FiAlertCircle className="text-red-600 text-3xl" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">رمز غير صالح</h2>
          <p className="text-gray-500">لم يتم العثور على مستخدم بهذا الرمز</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center" dir="rtl">
        <div className="text-center p-8 max-w-sm">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiCheckCircle className="text-green-600 text-4xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">تم التحويل بنجاح!</h2>
          <p className="text-gray-500 mb-2">
            تم تحويل <span className="font-bold text-gray-900">{formatCurrency(parseFloat(amount))}</span> إلى
          </p>
          <p className="font-bold text-gray-800">{receiverInfo.user?.fullName || 'المستلم'}</p>
          <p className="text-xs text-gray-400 mt-4">سيتم إغلاق هذه النافذة تلقائياً...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" dir="rtl">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="flex items-center justify-between px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 transition active:scale-95"
          >
            <FiArrowLeft className="text-gray-700 w-5 h-5" />
          </button>
          <h1 className="text-gray-900 font-bold text-lg">تحويل الأموال</h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="flex-1 p-4">
        <div className="max-w-md mx-auto">
          {/* Receiver Info Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-5">
            <div className="flex items-center gap-4">
              <img
                src={receiverInfo.user?.avatarUrl || 'https://via.placeholder.com/150'}
                alt="Receiver"
                className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 text-lg truncate">
                  {receiverInfo.user?.fullName || 'مستخدم'}
                </h3>
                <p className="text-sm text-gray-500">
                  {receiverInfo.userType === 'provider' ? 'مزود خدمة' : 'زبون'}
                </p>
                <span className="inline-block mt-1 bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  {receiverInfo.user?.walletCode || ''}
                </span>
              </div>
            </div>
          </div>

          {/* Amount Input Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-5">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              المبلغ المراد تحويله (د.ج)
            </label>
            <div className="relative">
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="أدخل المبلغ"
                className="w-full py-4 px-4 text-2xl font-extrabold text-gray-900 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-center transition"
                dir="ltr"
              />
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
                د.ج
              </span>
            </div>

            {/* Available Balance */}
            {senderProfile && (
              <div className="mt-3 flex items-center justify-between bg-gray-50 rounded-xl p-3">
                <span className="text-xs text-gray-500 font-medium">الرصيد المتاح:</span>
                <span className={`text-sm font-bold ${(senderProfile.wallet || 0) >= (parseFloat(amount) || 0) ? 'text-green-600' : 'text-red-500'}`}>
                  {formatCurrency(senderProfile.wallet || 0)}
                </span>
              </div>
            )}

            {/* Validation message */}
            {amount && parseFloat(amount) > 0 && senderProfile && (
              <div className="mt-2">
                {parseFloat(amount) > (senderProfile.wallet || 0) ? (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <FiAlertCircle className="w-3 h-3" />
                    الرصيد غير كافٍ
                  </p>
                ) : (
                  <p className="text-green-600 text-xs flex items-center gap-1">
                    <FiCheckCircle className="w-3 h-3" />
                    الرصيد كافٍ - الرصيد بعد التحويل: {formatCurrency((senderProfile.wallet || 0) - parseFloat(amount))}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Send Button */}
          <button
            onClick={handleSendTransfer}
            disabled={sending || !amount || parseFloat(amount) <= 0 || (senderProfile && parseFloat(amount) > (senderProfile.wallet || 0))}
            className="w-full bg-gradient-to-l from-gray-900 to-gray-700 text-white py-4 rounded-2xl text-base font-bold hover:opacity-90 transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-gray-200 flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                جاري التحويل...
              </>
            ) : (
              <>
                <FiSend className="w-5 h-5" />
                تأكيد التحويل
              </>
            )}
          </button>

          {/* Info */}
          <div className="mt-6 bg-blue-50 border border-blue-100 rounded-2xl p-4">
            <p className="text-xs text-blue-700 leading-relaxed">
              سيتم تحويل المبلغ مباشرة من محفظتك إلى محفظة المستخدم. لا يمكن التراجع عن هذه العملية.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrTransferScreen;